import { useCallback, useEffect, useMemo, useState } from 'react'

import { CONTAINER_MAX_WIDTH } from '@/config/constants'
import { userListRootKey } from '@/hooks/keyUtils'
import useServiceStatus from '@/hooks/useServiceStatus'
import AppBar from '@/pages/projectList/components/AppBar'
import Footer from '@/pages/projectList/components/Footer'
import NotificationStrip from '@/pages/projectList/components/NotificationStrip'
import useUser from '@/pages/projectList/hooks/useUser'
import { isValidEmail } from '@/pages/projectList/utils'
import { toaster } from '@/system/toaster'
import { isAdminUser } from '@/utils/user'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Flex } from '@chakra-ui/react'
import { LoadingPage } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import { patchUsers } from '@infraloop/services'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router'

import UserListPagination from './components/UserListPagination'
import UserListTable from './components/UserListTable'
import UserListToolbar from './components/UserListToolbar'
import useUserList from './hooks/useUserList'
import { SortDirection, SortState, cycleSortState } from './utils'

/** Delay before an Auth0-email search fires, so typing doesn't spam the endpoint. */
const SEARCH_DEBOUNCE_MS = 400

/** Number of users fetched per keyset page. */
const USER_LIST_PAGE_SIZE = 30

const UserList = () => {
  // Hooks
  const { t } = useTranslation()
  const { user, isLoadingUser } = useUser()
  const { getAccessTokenSilently } = useAuth0()
  const { serviceStatus } = useServiceStatus()
  const queryClient = useQueryClient()

  // States
  const [searchInput, setSearchInput] = useState('')
  const [committedEmail, setCommittedEmail] = useState('')
  const [sortDir, setSortDir] = useState<SortState>(SortDirection.Desc)
  // Keyset pagination: pageKeys[i] is the cursor used to fetch page i (page 0 has none).
  const [page, setPage] = useState(0)
  const [pageKeys, setPageKeys] = useState<(Record<string, string> | undefined)[]>([undefined])
  const [editMode, setEditMode] = useState(false)
  const [changedHubspotIds, setChangedHubspotIds] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  // Derived state
  const isAdmin = isAdminUser(user)
  const { users, lastEvaluatedKey, isLoading, isFetching } = useUserList({
    email: committedEmail,
    sortDir,
    limit: USER_LIST_PAGE_SIZE,
    lastEvaluatedKey: pageKeys[page],
    enabled: isAdmin,
  })
  const usersMap = useMemo(() => new Map(users.map((u) => [u.user_id, u])), [users])
  const editCount = Object.keys(changedHubspotIds).length
  const notification = serviceStatus?.notification?.enabled ? serviceStatus.notification : undefined

  /**
   * Reset paging back to the first page. Called whenever a filter, search, or sort changes,
   * since the existing keyset cursors no longer apply.
   */
  const resetPagination = useCallback(() => {
    setPage(0)
    setPageKeys([undefined])
  }, [])

  /**
   * Debounce the search box into the committed email. The endpoint requires a full valid
   * email, so a valid email is committed as the filter and anything else (partial or empty)
   * clears it — this keeps the shown results in step with the box instead of leaving a stale
   * filter applied while the text says otherwise.
   */
  useEffect(() => {
    const trimmed = searchInput.trim()
    const nextCommitted = isValidEmail(trimmed) ? trimmed : ''
    if (nextCommitted === committedEmail) return undefined
    const timer = setTimeout(() => {
      setCommittedEmail(nextCommitted)
      resetPagination()
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput, committedEmail, resetPagination])

  /**
   * Step the subscription-end sort to its next state (descending, ascending, unsorted).
   */
  const handleToggleSort = useCallback(() => {
    setSortDir((prev) => cycleSortState(prev))
    resetPagination()
  }, [resetPagination])

  /**
   * Refetch the current list (the toolbar's refresh control).
   */
  const handleRefetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: userListRootKey() })
  }, [queryClient])

  /**
   * Advance to the next page using the current page's keyset cursor.
   */
  const handleNext = useCallback(() => {
    if (!lastEvaluatedKey) return
    setPageKeys((prev) => {
      const next = [...prev]
      next[page + 1] = lastEvaluatedKey
      return next
    })
    setPage((prev) => prev + 1)
  }, [lastEvaluatedKey, page])

  /**
   * Go back to the previous page (its cursor is already stored).
   */
  const handlePrev = useCallback(() => {
    setPage((prev) => Math.max(0, prev - 1))
  }, [])

  /**
   * Enter HubSpot-ID edit mode, starting from a clean set of changes.
   */
  const handleEnterEdit = useCallback(() => {
    setChangedHubspotIds({})
    setEditMode(true)
  }, [])

  /**
   * Discard pending changes and leave edit mode.
   */
  const handleCancel = useCallback(() => {
    setChangedHubspotIds({})
    setEditMode(false)
  }, [])

  /**
   * Track a row's pending HubSpot ID, dropping it when it matches the saved value again.
   * @param userId Id of the user being edited.
   * @param value New HubSpot ID input value.
   */
  const handleHubspotIdChange = useCallback(
    (userId: string, value: string) => {
      const original = usersMap.get(userId)?.hubspot_id ?? ''
      setChangedHubspotIds((prev) => {
        const next = { ...prev }
        if (value === original) {
          delete next[userId]
        } else {
          next[userId] = value
        }
        return next
      })
    },
    [usersMap],
  )

  /**
   * Undo a single row's pending HubSpot-ID change.
   * @param userId Id of the user whose change to discard.
   */
  const handleClearHubspotId = useCallback((userId: string) => {
    setChangedHubspotIds((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
  }, [])

  /**
   * Persist all pending HubSpot-ID changes, then refresh the list and leave edit mode.
   */
  const handleSave = useCallback(async () => {
    const changes = Object.entries(changedHubspotIds).map(([user_id, hubspot_id]) => ({ user_id, hubspot_id }))
    if (changes.length === 0) return

    setIsSaving(true)
    try {
      const accessToken = await getAccessTokenSilently()
      await patchUsers(accessToken, changes)
      await queryClient.invalidateQueries({ queryKey: userListRootKey() })
      setChangedHubspotIds({})
      setEditMode(false)
      toaster.create({ type: 'success', title: t('hatsuly.userList.saved') })
    } catch {
      toaster.create({ type: 'error', title: t('hatsuly.userList.saveError') })
    } finally {
      setIsSaving(false)
    }
  }, [changedHubspotIds, getAccessTokenSilently, queryClient, t])

  if (isLoadingUser) {
    return <LoadingPage text={t('ui.loading')} />
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <Flex direction="column" minH="100vh" bg="#F6F8FB">
      {notification && <NotificationStrip notification={notification} />}
      <AppBar />
      <Box as="main" flex="1" w="100%" maxW={CONTAINER_MAX_WIDTH} mx="auto" px="28px" pt="30px" pb="64px">
        <UserListToolbar
          editMode={editMode}
          editCount={editCount}
          search={searchInput}
          isSaving={isSaving}
          isFetching={isFetching}
          onEnterEdit={handleEnterEdit}
          onSave={() => {
            void handleSave()
          }}
          onCancel={handleCancel}
          onRefetch={handleRefetch}
          onSearchChange={setSearchInput}
        />
        <UserListTable
          users={users}
          editMode={editMode}
          changedHubspotIds={changedHubspotIds}
          sortDir={sortDir}
          isLoading={isLoading}
          isFetching={isFetching}
          onToggleSort={handleToggleSort}
          onHubspotIdChange={handleHubspotIdChange}
          onClearHubspotId={handleClearHubspotId}
        />
        <UserListPagination
          hasPrev={page > 0}
          hasNext={Boolean(lastEvaluatedKey)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      </Box>
      <Footer />
    </Flex>
  )
}

export default UserList
